<script lang="ts">
  import BaseScene from "@engine/BaseScene.svelte";
  import { TRADEMARK_LEGAL_NOTICE, COPYRIGHT_LINE, IMPRINT_ATTRIBUTION } from "@config/constants/branding";
  import legal from "@config/content/legal.json";
  import themesList from "@config/content/themes_list.json";
  import gameInfo from "@config/game_info.json";

  export let onBack: () => void;

  let tab: "health" | "content" | "legal" = "health";
</script>

<BaseScene sceneId="hcl" ariaLabel="Health, Content and Legal" onEscape={onBack}>
  <div class="hcl">
    <h1>Health, Content and Legal</h1>

    <div class="hcl__tabs" role="tablist">
      <button type="button" role="tab" aria-selected={tab === "health"} on:click={() => (tab = "health")}>Health</button>
      <button type="button" role="tab" aria-selected={tab === "content"} on:click={() => (tab = "content")}>Content</button>
      <button type="button" role="tab" aria-selected={tab === "legal"} on:click={() => (tab = "legal")}>Legal</button>
    </div>

    {#if tab === "health"}
      <section aria-label="Health information">
        <h2>Photosensitive epilepsy</h2>
        <p>This game uses colorful animation and short flashes. If you or a family member has a history of photosensitive epilepsy, play at a distance and take breaks.</p>
        <h2>For parents and caregivers</h2>
        <p>{gameInfo.brand} {gameInfo.productSubtitle} is designed for kids ages {gameInfo.ageRange}. It has no ads, no purchases, no accounts, and no data collection. Progress saves on the device only. See the Content and Legal tabs for details.</p>
        <p>Common Sense Media reviews kids' apps and games:
          <a href={legal.commonSenseMediaUrl} target="_blank" rel="noopener noreferrer">commonsensemedia.org</a>
        </p>
      </section>
    {:else if tab === "content"}
      <section aria-label="Content list">
        <p>{themesList.intro}</p>
        <h2>Themes in this game</h2>
        <ul>
          {#each themesList.themes as theme (theme.id)}
            <li>
              <strong>{theme.label}.</strong> {theme.note}
            </li>
          {/each}
        </ul>
        <p><em>{themesList.notes}</em></p>
      </section>
    {:else}
      <section aria-label="Legal notices">
        <h2>Product</h2>
        <p>{legal.productName}</p>
        <p>{IMPRINT_ATTRIBUTION}</p>

        <h2>Trademark</h2>
        <p>{TRADEMARK_LEGAL_NOTICE}</p>

        <h2>Copyright</h2>
        <p>{COPYRIGHT_LINE}</p>

        <h2>Privacy</h2>
        <p>{legal.privacyIntro}</p>
        <p>{legal.coppaStatement}</p>

        <h2>Age rating</h2>
        <p>{legal.ageRatingNote}</p>

        <h2>Terms</h2>
        <p>{legal.termsIntro}</p>
      </section>
    {/if}

    <button type="button" class="hcl__back" on:click={onBack}>Back</button>
  </div>
</BaseScene>

<style>
  .hcl {
    max-width: 720px;
    margin: 0 auto;
    padding: 2rem 1rem;
  }
  .hcl h1 {
    color: var(--color-heading);
    text-align: center;
    margin-bottom: 1.5rem;
  }
  .hcl__tabs {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    border-bottom: 1px solid var(--color-divider);
  }
  .hcl__tabs button {
    padding: 0.75em 1.5em;
    background: transparent;
    border: none;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    font-size: 1rem;
    min-height: 44px;
  }
  .hcl__tabs button[aria-selected="true"] {
    border-bottom-color: var(--color-accent);
    color: var(--color-accent);
    font-weight: 600;
  }
  .hcl h2 {
    color: var(--color-heading);
    font-size: 1.15rem;
    margin: 1rem 0 0.5rem;
  }
  .hcl p, .hcl li {
    text-align: left;
  }
  .hcl__back {
    display: block;
    margin: 2rem auto 0;
    min-height: 44px;
    padding: 0.5em 1.5em;
    background: var(--color-surface);
    border: 1px solid var(--color-divider);
    border-radius: 0.5em;
    cursor: pointer;
  }
</style>
